import { Fragment, useEffect, useMemo, useRef, useState } from "react"
import { Loader2, Trash2, AlertTriangle, ChevronRight, ChevronDown, ExternalLink, Pencil } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { cn } from "@/lib/utils"
import { formatEur } from "@/lib/format"
import { AcCatalogTab } from "@/components/settings/AcCatalogTab"
import { BcCatalogTab } from "@/components/settings/BcCatalogTab"
import { ProductSearch } from "@/components/mappings/ProductSearch"
import {
  listRoleProducts,
  assignProductToRole,
  unassignProductFromRole,
  setMappingProductActive,
  updateProductMetadata,
  listBrands,
  listStructureBom,
  setBomQty,
  removeBomComponent,
  addBomComponent,
  fetchSettings,
  patchSettings,
  type MappingProductRow,
  type MappingSearchRow,
  type BomRow,
} from "@/lib/api"

interface BrandOption {
  slug: string
  label: string
}

const MODULES = [
  { value: "solar", label: "Solar", enabled: true },
  { value: "ac", label: "AC", enabled: true },
  { value: "bc_aqs", label: "BC + AQS", enabled: true },
  { value: "bc_aq", label: "BC (só AQ)", enabled: true },
]

const SOLAR_ROLES = [
  { value: "panel", label: "Painéis" },
  { value: "inverter", label: "Inversores" },
  { value: "battery", label: "Baterias" },
  { value: "battery_controller", label: "Controlador Bateria" },
  { value: "structure", label: "Estruturas" },
  { value: "meter", label: "Medidores" },
  { value: "counter", label: "Contadores" },
  { value: "charger", label: "Carregadores" },
  { value: "backup", label: "Backup" },
  { value: "installation", label: "Instalação" },
  { value: "installation_backup", label: "Instalação Backup" },
  { value: "installation_charger", label: "Instalação Carregador" },
  { value: "labour", label: "Mão de Obra" },
  { value: "accessory", label: "Monitorização" },
]

// Grupos sem margem (só IVA) — espelha NO_MARGIN_ROLES em
// functions/lib/engine/solar-metadata.ts. Regra de negócio fixa: instalação e
// mão-de-obra são custos de execução, não produtos, por isso não levam margem.
const NO_MARGIN_ROLES = new Set([
  "installation",
  "installation_backup",
  "installation_charger",
  "labour",
])

// Campos de metadata editáveis por role (os que o motor usa na simulação).
type FieldType = "number" | "phase" | "numlist" | "sluglist" | "structure_type"
interface FieldDef {
  key: string
  label: string
  type: FieldType
}
const ROLE_FIELDS: Record<string, FieldDef[]> = {
  panel: [
    { key: "wattage_w", label: "Wp", type: "number" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  inverter: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "power_kw", label: "Pot. kW", type: "number" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  battery: [
    { key: "capacity_wh", label: "Cap. Wh", type: "number" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
    { key: "compatible_powers_kva", label: "Pot. kVA", type: "numlist" },
  ],
  battery_controller: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  backup: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  charger: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "power_kw", label: "Pot. kW", type: "number" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  structure: [
    { key: "structure_type", label: "Tipo estrutura", type: "structure_type" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  // Medidor entra sempre no kit, mas escolhido pela marca (Solis não tem
  // medidor → sem candidato → sem linha). Precisa do multiselect de marcas.
  meter: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
  // Contador: produto geral (universal), entra com o visto "Tem contador".
  counter: [{ key: "phase", label: "Fase", type: "phase" }],
  installation: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_powers_kva", label: "Pot. kVA", type: "numlist" },
  ],
  installation_backup: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_powers_kva", label: "Pot. kVA", type: "numlist" },
  ],
  installation_charger: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_powers_kva", label: "Pot. kVA", type: "numlist" },
  ],
  labour: [{ key: "labour_technicians", label: "Técnicos", type: "number" }],
  accessory: [
    { key: "phase", label: "Fase", type: "phase" },
    { key: "compatible_brand_slugs", label: "Marcas compat.", type: "sluglist" },
  ],
}

function asSlugArray(v: unknown): string[] {
  // Normaliza para minúsculas — os slugs guardados (migrados) podem vir noutro
  // case e os slugs das marcas (solar_slug) são sempre minúsculas.
  const norm = (s: unknown) => String(s).trim().toLowerCase()
  if (Array.isArray(v)) return v.map(norm).filter(Boolean)
  if (typeof v === "string" && v.trim()) return v.split(",").map(norm).filter(Boolean)
  return []
}

function metaToStr(value: unknown, type: FieldType): string {
  if (value == null) return ""
  if (type === "numlist" || type === "sluglist") {
    return Array.isArray(value) ? value.join(", ") : String(value)
  }
  // Fase migrada pode vir "TRI"/"Mono"; normaliza p/ o select casar (mono/tri).
  if (type === "phase" || type === "structure_type") {
    return String(value).trim().toLowerCase()
  }
  return String(value)
}

/** Devolve o valor coerido, ou undefined para remover a chave (vazio). */
function strToMeta(raw: string, type: FieldType): unknown {
  const s = raw.trim()
  if (s === "") return undefined
  if (type === "number") {
    const n = Number(s.replace(",", "."))
    return Number.isFinite(n) ? n : undefined
  }
  if (type === "phase") return s === "mono" || s === "tri" ? s : undefined
  if (type === "structure_type") {
    return s === "triangular" || s === "coplanar" ? s : undefined
  }
  if (type === "numlist") {
    const list = s
      .split(",")
      .map((t) => Number(t.trim().replace(/\s/g, "").replace(",", ".")))
      .filter((n) => Number.isFinite(n))
    return list.length ? list : undefined
  }
  // sluglist
  const list = s
    .split(",")
    .map((t) => t.trim().toLowerCase())
    .filter(Boolean)
  return list.length ? list : undefined
}

interface MapeamentosProps {
  shell: AppShell | null
}

export function Mapeamentos({ shell }: MapeamentosProps) {
  // Abre o detalhe do produto no CRM host em NOVA JANELA (para não perder o
  // contexto dos mapeamentos). Usa o hostOrigin do contexto (o origin do CRM;
  // window.location.origin aqui é o CDN dos assets, não serve). O sandbox do
  // iframe permite popups (allow-popups-to-escape-sandbox). Sem hostOrigin,
  // cai no navigateCrm (mesma janela) como antes.
  function openProduct(productId: number) {
    const ctx = shell?.context as
      | { locale?: string; hostOrigin?: string }
      | undefined
    const locale = ctx?.locale ?? "pt"
    const path = `/${locale}/products/${productId}`
    if (ctx?.hostOrigin) {
      window.open(`${ctx.hostOrigin}${path}`, "_blank", "noopener")
    } else {
      shell?.bridge.navigateCrm({ href: path })
    }
  }

  const [module, setModule] = useState("solar")
  const [role, setRole] = useState("backup")
  const [rows, setRows] = useState<MappingProductRow[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busyId, setBusyId] = useState<number | null>(null)
  const [showInactive, setShowInactive] = useState(false)
  const [brandFilter, setBrandFilter] = useState("__all__")
  const [brands, setBrands] = useState<BrandOption[]>([])
  const [expandedId, setExpandedId] = useState<number | null>(null)
  // Ordem por role (settings.role_order): ordena as linhas do kit na geração.
  const [roleOrder, setRoleOrder] = useState<Record<string, number>>({})
  const [savingOrder, setSavingOrder] = useState(false)

  useEffect(() => {
    if (!shell) return
    fetchSettings(shell)
      .then((s) => setRoleOrder(s.role_order ?? {}))
      .catch(() => setRoleOrder({}))
  }, [shell])

  async function saveRoleOrder(next: Record<string, number>) {
    setRoleOrder(next)
    if (!shell) return
    setSavingOrder(true)
    try {
      await patchSettings(shell, { role_order: next })
    } catch (err) {
      shell.bridge.toast({
        variant: "error",
        description: err instanceof Error ? err.message : "Falha a guardar ordem",
      })
    } finally {
      setSavingOrder(false)
    }
  }

  // Roles ordenados pela ordem definida (sem ordem → fim, por label).
  const sortedRoles = useMemo(
    () =>
      [...SOLAR_ROLES].sort((a, b) => {
        const oa = roleOrder[a.value] ?? 999
        const ob = roleOrder[b.value] ?? 999
        return oa - ob || a.label.localeCompare(b.label)
      }),
    [roleOrder],
  )

  const roleLabel = useMemo(
    () => SOLAR_ROLES.find((r) => r.value === role)?.label ?? role,
    [role],
  )
  const fields = ROLE_FIELDS[role] ?? []
  const roleNoMargin = NO_MARGIN_ROLES.has(role)
  const activeCount = rows.filter((r) => r.active).length
  const inactiveCount = rows.length - activeCount
  // O filtro por marca só faz sentido nos roles que têm compatibilidade de marca.
  const hasBrandField = fields.some((f) => f.key === "compatible_brand_slugs")
  const visibleRows = (showInactive ? rows : rows.filter((r) => r.active)).filter(
    (r) => {
      if (!hasBrandField || brandFilter === "__all__") return true
      const slugs = asSlugArray(r.metadata?.compatible_brand_slugs)
      // Lista vazia = universal (compatível com todas as marcas) → mostra sempre.
      return slugs.length === 0 || slugs.includes(brandFilter)
    },
  )

  async function reload() {
    if (!shell) return
    setLoading(true)
    setError(null)
    try {
      setRows(await listRoleProducts(shell, role))
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha a carregar")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    reload()
    setExpandedId(null)
    setBrandFilter("__all__")
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, role])

  // Marcas ativas (com slug solar) para o multiselect de compatibilidade.
  useEffect(() => {
    if (!shell) return
    listBrands(shell)
      .then((rows) =>
        setBrands(
          rows
            .filter((b) => b.is_active !== false && b.solar_slug)
            .map((b) => ({ slug: b.solar_slug as string, label: b.brand_name }))
            .sort((a, b) => a.label.localeCompare(b.label)),
        ),
      )
      .catch(() => setBrands([]))
  }, [shell])

  function toggleExpand(row: MappingProductRow) {
    setExpandedId((cur) => (cur === row.product_id ? null : row.product_id))
  }

  async function toggleActive(row: MappingProductRow, next: boolean) {
    if (!shell) return
    setBusyId(row.product_id)
    try {
      await setMappingProductActive(shell, row.product_id, next)
      setRows((prev) => prev.map((r) => (r.product_id === row.product_id ? { ...r, active: next } : r)))
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    } finally {
      setBusyId(null)
    }
  }

  async function removeFromRole(row: MappingProductRow) {
    if (!shell) return
    setBusyId(row.product_id)
    try {
      await unassignProductFromRole(shell, row.product_id)
      setRows((prev) => prev.filter((r) => r.product_id !== row.product_id))
      shell.bridge.toast({ variant: "success", description: "Removido do grupo" })
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    } finally {
      setBusyId(null)
    }
  }

  async function addProduct(p: MappingSearchRow) {
    if (!shell) return
    try {
      await assignProductToRole(shell, p.product_id, role)
      shell.bridge.toast({
        variant: "success",
        description: `"${p.alt_name || p.name}" ligado a ${roleLabel}`,
      })
      await reload()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a ligar" })
    }
  }

  // Guarda a metadata de uma linha (após editar uma célula). Constrói o objeto
  // coerido a partir dos campos do role e persiste.
  async function saveMetadata(row: MappingProductRow) {
    if (!shell) return
    const clean: Record<string, unknown> = { ...row.metadata }
    for (const f of fields) {
      const v = row.metadata[f.key]
      let coerced: unknown
      if (Array.isArray(v)) coerced = v.length > 0 ? v : undefined // já tipado (multiselect)
      else if (typeof v === "number") coerced = v
      else coerced = strToMeta(metaToStr(v, f.type), f.type)
      if (coerced === undefined) delete clean[f.key]
      else clean[f.key] = coerced
    }
    try {
      await updateProductMetadata(shell, row.product_id, clean)
      setRows((prev) => prev.map((r) => (r.product_id === row.product_id ? { ...r, metadata: clean } : r)))
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a guardar" })
    }
  }

  // Set an array-valued metadata field (multiselect) and persist immediately.
  function setBrandSlugs(row: MappingProductRow, slugs: string[]) {
    const updated: MappingProductRow = {
      ...row,
      metadata: { ...row.metadata, compatible_brand_slugs: slugs },
    }
    setRows((prev) => prev.map((r) => (r.product_id === row.product_id ? updated : r)))
    void saveMetadata(updated)
  }

  function patchCell(productId: number, key: string, raw: string) {
    setRows((prev) =>
      prev.map((r) =>
        r.product_id === productId ? { ...r, metadata: { ...r.metadata, [key]: raw } } : r,
      ),
    )
  }

  const ModuleSelector = (
    <div className="flex flex-wrap gap-2">
      {MODULES.map((m) => (
        <button
          key={m.value}
          disabled={!m.enabled}
          onClick={() => m.enabled && setModule(m.value)}
          className={cn(
            "rounded-md border px-3 py-1.5 text-sm transition",
            module === m.value
              ? "border-primary bg-primary text-primary-foreground"
              : "border-border hover:bg-muted",
            !m.enabled && "cursor-not-allowed opacity-40",
          )}
        >
          {m.label}
          {!m.enabled && <span className="ml-1 text-xs">(em breve)</span>}
        </button>
      ))}
    </div>
  )

  // Módulo AC: catálogo app-owned (tabela dedicada), UI própria.
  if (module === "ac") {
    return (
      <div className="mx-auto max-w-6xl space-y-5 p-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mapeamentos</h1>
          <p className="text-sm text-muted-foreground">
            Gere que produtos alimentam cada grupo do simulador e a sua metadata técnica.
          </p>
        </div>
        {ModuleSelector}
        <AcCatalogTab shell={shell} onOpenProduct={openProduct} />
      </div>
    )
  }

  // Bombas de calor: catálogo app-owned (bc_products), UI própria por módulo.
  if (module === "bc_aq" || module === "bc_aqs") {
    return (
      <div className="mx-auto max-w-6xl space-y-5 p-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Mapeamentos</h1>
          <p className="text-sm text-muted-foreground">
            Gere que produtos alimentam cada grupo do simulador e a sua metadata técnica.
          </p>
        </div>
        {ModuleSelector}
        <BcCatalogTab shell={shell} kind={module} onOpenProduct={openProduct} />
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-5 p-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Mapeamentos</h1>
        <p className="text-sm text-muted-foreground">
          Gere que produtos alimentam cada grupo do simulador e a sua metadata técnica.
        </p>
      </div>

      {ModuleSelector}

      <div className="flex flex-wrap gap-1.5">
        {sortedRoles.map((r) => (
          <button
            key={r.value}
            onClick={() => setRole(r.value)}
            className={cn(
              "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition",
              role === r.value
                ? "border-primary bg-primary/10 font-medium text-primary"
                : "border-border text-muted-foreground hover:bg-muted",
            )}
          >
            {roleOrder[r.value] != null && (
              <span className="rounded bg-muted px-1 text-[10px] tabular-nums text-muted-foreground">
                {roleOrder[r.value]}
              </span>
            )}
            {r.label}
          </button>
        ))}
      </div>

      <ProductSearch
        shell={shell}
        onPick={addProduct}
        placeholder="Adicionar produto ao grupo — pesquisa por nome ou SKU…"
        taken={(p) => (rows.some((r) => r.product_id === p.product_id) ? "neste grupo" : null)}
        hint={(p) => (p.current_role ? `já em ${p.current_role}` : null)}
      />

      <div className="space-y-2">
        <div className="flex items-center justify-between gap-3">
          <h2 className="flex flex-wrap items-center gap-2 text-sm font-medium">
            {roleLabel}
            <span className="text-muted-foreground">
              {activeCount} ativos{inactiveCount > 0 && ` · ${inactiveCount} inativos`}
            </span>
            <span
              className={cn(
                "rounded-full border px-2 py-0.5 text-xs font-normal",
                roleNoMargin
                  ? "border-border text-muted-foreground"
                  : "border-primary/40 text-primary",
              )}
              title={
                roleNoMargin
                  ? "Grupo sem margem — passa a custo, só com IVA (regra fixa: instalação/mão-de-obra)"
                  : "Grupo com margem — aplica a margem da marca (Configurações → Marcas Solar)"
              }
            >
              {roleNoMargin ? "sem margem · só IVA" : "com margem da marca"}
            </span>
          </h2>
          <div className="flex items-center gap-3">
            <label
              className="flex items-center gap-1.5 text-xs text-muted-foreground"
              title="Ordem deste role nas linhas do kit (UI + PDF). Menor = primeiro."
            >
              Ordem
              <Input
                type="number"
                step="1"
                min={0}
                value={roleOrder[role] ?? ""}
                onChange={(e) => {
                  const raw = e.target.value
                  const next = { ...roleOrder }
                  if (raw === "") delete next[role]
                  else next[role] = Math.max(0, Number(raw) || 0)
                  setRoleOrder(next)
                }}
                onBlur={() => saveRoleOrder(roleOrder)}
                className="h-8 w-16 text-right"
              />
              {savingOrder && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            </label>
            {hasBrandField && brands.length > 0 && (
              <Select value={brandFilter} onValueChange={setBrandFilter}>
                <SelectTrigger className="h-8 w-44 text-xs">
                  <SelectValue placeholder="Marca" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas as marcas</SelectItem>
                  {brands.map((b) => (
                    <SelectItem key={b.slug} value={b.slug}>
                      {b.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            {inactiveCount > 0 && (
              <label className="flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
                <Switch checked={showInactive} onCheckedChange={setShowInactive} />
                Mostrar inativos
              </label>
            )}
            {loading && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          </div>
        </div>

        {error ? (
          <p className="text-sm text-destructive">{error}</p>
        ) : (
          <div className="overflow-x-auto rounded-md border border-border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-40">SKU</TableHead>
                  <TableHead className="min-w-48">Nome público</TableHead>
                  <TableHead className="w-28 text-right" title="Custo do Odoo (sem IVA). Altera-se no produto (no Odoo, ou em «Preço de custo» no CRM).">Custo (€)</TableHead>
                  {fields.map((f) => (
                    <TableHead key={f.key} className="w-32">{f.label}</TableHead>
                  ))}
                  <TableHead className="w-20 text-center">Ativo</TableHead>
                  <TableHead className="w-14 text-right">Rem.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visibleRows.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={fields.length + 5} className="text-center text-sm text-muted-foreground">
                      {rows.length === 0
                        ? "Nenhum produto neste grupo. Usa a pesquisa acima para adicionar."
                        : "Nenhum produto ativo. Liga “Mostrar inativos” para ver os restantes."}
                    </TableCell>
                  </TableRow>
                ) : (
                  visibleRows.map((row) => {
                    const isStructure = role === "structure"
                    const expanded = expandedId === row.product_id
                    return (
                    <Fragment key={row.product_id}>
                    <TableRow className={cn(!row.active && "opacity-55")}>
                      <TableCell className="font-mono text-xs">
                        {row.exists_in_crm !== false ? (
                          <button
                            type="button"
                            onClick={() => openProduct(row.product_id)}
                            title="Abrir produto no CRM"
                            className="inline-flex items-center gap-1 text-accent hover:underline"
                          >
                            {row.sku || "ver produto"}
                            <ExternalLink className="h-3 w-3 opacity-70" />
                          </button>
                        ) : (
                          row.sku || <span className="text-muted-foreground">— sem SKU</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1.5 font-medium">
                          {isStructure && (
                            <button
                              onClick={() => toggleExpand(row)}
                              title="Ver componentes"
                              className="shrink-0 text-muted-foreground hover:text-foreground"
                            >
                              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                            </button>
                          )}
                          {!row.exists_in_crm && (
                            <AlertTriangle className="h-3.5 w-3.5 shrink-0 text-amber-500" />
                          )}
                          {row.alt_name || row.name}
                        </div>
                        <CostMarginLine row={row} />
                      </TableCell>
                      <TableCell className="text-right tabular-nums">
                        <CostCell row={row} onEdit={row.exists_in_crm !== false ? () => openProduct(row.product_id) : undefined} />
                      </TableCell>
                      {fields.map((f) => (
                        <TableCell key={f.key}>
                          {f.key === "compatible_brand_slugs" ? (
                            <BrandMultiSelect
                              options={brands}
                              value={asSlugArray(row.metadata[f.key])}
                              onChange={(slugs) => setBrandSlugs(row, slugs)}
                            />
                          ) : f.type === "phase" ? (
                            <select
                              value={metaToStr(row.metadata[f.key], f.type)}
                              onChange={(e) => patchCell(row.product_id, f.key, e.target.value)}
                              onBlur={() => saveMetadata(row)}
                              className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            >
                              <option value="">—</option>
                              <option value="mono">Mono</option>
                              <option value="tri">Tri</option>
                            </select>
                          ) : f.type === "structure_type" ? (
                            <select
                              value={metaToStr(row.metadata[f.key], f.type)}
                              onChange={(e) => patchCell(row.product_id, f.key, e.target.value)}
                              onBlur={() => saveMetadata(row)}
                              className="h-8 w-full rounded-md border border-input bg-background px-2 text-xs"
                            >
                              <option value="">—</option>
                              <option value="triangular">Triangular</option>
                              <option value="coplanar">Coplanar</option>
                            </select>
                          ) : (
                            <Input
                              value={metaToStr(row.metadata[f.key], f.type)}
                              onChange={(e) => patchCell(row.product_id, f.key, e.target.value)}
                              onBlur={() => saveMetadata(row)}
                              placeholder={f.type === "numlist" ? "3.45, 6.9" : ""}
                              className="h-8 text-xs"
                            />
                          )}
                        </TableCell>
                      ))}
                      <TableCell className="text-center">
                        <Switch
                          checked={row.active}
                          disabled={busyId === row.product_id}
                          onCheckedChange={(v) => toggleActive(row, v)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="icon"
                          variant="ghost"
                          disabled={busyId === row.product_id}
                          onClick={() => removeFromRole(row)}
                          title="Remover do grupo"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                    {isStructure && expanded && (
                      <TableRow className="bg-muted/40">
                        <TableCell colSpan={fields.length + 5} className="py-3">
                          <StructureBomEditor shell={shell} structureProductId={row.product_id} />
                        </TableCell>
                      </TableRow>
                    )}
                    </Fragment>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}
        {fields.length === 0 && rows.length > 0 && (
          <p className="text-xs text-muted-foreground">Este grupo não tem metadata técnica editável.</p>
        )}
      </div>
    </div>
  )
}

// ── Linha de validação: custo (Odoo) + margem (marca) ────────────────────

/**
 * Custo do Odoo (sem IVA) em coluna, como nos catálogos AC/BC (pedido da Mais
 * Energia, 02/11). Não se edita aqui: o custo do solar vem do Odoo e a
 * sincronização escrevia-o por cima — o lápis abre o produto («Preço de custo»).
 */
function CostCell({ row, onEdit }: { row: MappingProductRow; onEdit?: () => void }) {
  const hasCost = row.cost_price != null && row.cost_price > 0
  return (
    <div className="flex items-center justify-end gap-1">
      {hasCost ? (
        <span>{formatEur(row.cost_price!)}</span>
      ) : (
        <span className="text-xs font-medium text-amber-600" title={row.base_price ? `Sem custo no Odoo — o simulador usa o preço base ${formatEur(row.base_price)}` : "Sem custo no Odoo"}>
          <AlertTriangle className="mr-0.5 inline h-3 w-3" />
          {row.base_price != null && row.base_price > 0 ? `base ${formatEur(row.base_price)}` : "sem custo"}
        </span>
      )}
      {onEdit && (
        <button type="button" onClick={onEdit} title="Alterar o custo no produto" aria-label="Alterar o custo no produto" className="text-muted-foreground hover:text-foreground">
          <Pencil className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  )
}

function CostMarginLine({ row }: { row: MappingProductRow }) {
  const fmtX = (n: number | null) => (n != null ? `×${n.toLocaleString("pt-PT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : null)

  // Margem: só-IVA (grupo sem margem) / margem da marca / margem default.
  // Quando a marca não tem margem definida (ou = 0), o motor aplica a margem
  // DEFAULT dos settings — não é "sem margem", é o fallback.
  let marginNode
  if (row.no_margin) {
    marginNode = <span className="text-muted-foreground">só IVA (sem margem)</span>
  } else {
    const s = fmtX(row.margin_without_battery)
    const c = fmtX(row.margin_with_battery)
    if (s || c) {
      marginNode = (
        <span className="text-muted-foreground">
          margem {s ?? "—"}
          {c && c !== s ? ` / ${c}` : ""}
          <span className="ml-0.5 opacity-60">{row.brand_name ? `· ${row.brand_name}` : ""}</span>
        </span>
      )
    } else if (row.default_margin != null) {
      marginNode = (
        <span className="text-muted-foreground">
          margem default {fmtX(row.default_margin)}
          <span className="ml-0.5 opacity-60">
            {row.brand_name ? `· ${row.brand_name} sem margem própria` : ""}
          </span>
        </span>
      )
    } else {
      marginNode = (
        <span className="font-medium text-amber-600">
          <AlertTriangle className="mr-0.5 inline h-3 w-3" />
          sem margem nem default
        </span>
      )
    }
  }

  // O custo tem coluna própria (CostCell); aqui fica a margem que se lhe aplica.
  return (
    <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs">
      {marginNode}
    </div>
  )
}

// ── Multiselect de marcas compatíveis ────────────────────────────────────

interface BrandMultiSelectProps {
  options: BrandOption[]
  value: string[]
  onChange: (slugs: string[]) => void
}

function BrandMultiSelect({ options, value, onChange }: BrandMultiSelectProps) {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement | null>(null)
  const selected = new Set(value)

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [open])

  // Inclui marcas selecionadas que já não estão entre as ativas (para não as
  // esconder/perder silenciosamente).
  const optionSlugs = new Set(options.map((o) => o.slug))
  const extras: BrandOption[] = value
    .filter((s) => !optionSlugs.has(s))
    .map((s) => ({ slug: s, label: `${s} (inativa)` }))
  const allOptions = [...options, ...extras]

  const label =
    value.length === 0
      ? "Todas"
      : allOptions
          .filter((o) => selected.has(o.slug))
          .map((o) => o.label)
          .join(", ") || `${value.length} marca(s)`

  function toggle(slug: string) {
    onChange(selected.has(slug) ? value.filter((s) => s !== slug) : [...value, slug])
  }

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input bg-background px-2 text-left text-xs"
        title={label}
      >
        <span className={cn("truncate", value.length === 0 && "text-muted-foreground")}>{label}</span>
        <ChevronDown className="h-3 w-3 shrink-0 text-muted-foreground" />
      </button>
      {open && (
        <div className="absolute z-30 mt-1 max-h-56 w-48 overflow-auto rounded-md border border-border bg-popover p-1 shadow-md">
          {allOptions.length === 0 ? (
            <p className="px-2 py-1 text-xs text-muted-foreground">Sem marcas ativas</p>
          ) : (
            allOptions.map((o) => (
              <label
                key={o.slug}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-xs hover:bg-muted"
              >
                <input
                  type="checkbox"
                  checked={selected.has(o.slug)}
                  onChange={() => toggle(o.slug)}
                />
                {o.label}
              </label>
            ))
          )}
          {value.length > 0 && (
            <button
              onClick={() => onChange([])}
              className="mt-1 w-full rounded px-2 py-1 text-left text-xs text-muted-foreground hover:bg-muted"
            >
              Limpar (todas)
            </button>
          )}
        </div>
      )}
    </div>
  )
}

// ── Editor de BOM de estrutura (por nº de painéis) ───────────────────────

function StructureBomEditor({
  shell,
  structureProductId,
}: {
  shell: AppShell | null
  structureProductId: number
}) {
  const [rows, setRows] = useState<BomRow[]>([])
  const [panelCounts, setPanelCounts] = useState<number[]>([])
  const [selected, setSelected] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [newCount, setNewCount] = useState("")

  async function load() {
    if (!shell) return
    setLoading(true)
    try {
      const { items, panel_counts } = await listStructureBom(shell, structureProductId)
      setRows(items)
      setPanelCounts(panel_counts)
      setSelected((c) => (c != null && panel_counts.includes(c) ? c : panel_counts[0] ?? null))
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shell, structureProductId])

  const forCount = rows.filter((r) => r.n_panels === selected).sort((a, b) => a.sort_order - b.sort_order)
  const allCounts = Array.from(new Set([...panelCounts, ...(selected != null ? [selected] : [])])).sort((a, b) => a - b)

  async function saveQty(row: BomRow, qty: number) {
    if (!shell || qty <= 0 || qty === row.qty) return
    setRows((prev) => prev.map((r) => (r.id === row.id ? { ...r, qty } : r)))
    try {
      await setBomQty(shell, row.id, qty)
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
      load()
    }
  }
  async function remove(row: BomRow) {
    if (!shell) return
    try {
      await removeBomComponent(shell, row.id)
      setRows((prev) => prev.filter((r) => r.id !== row.id))
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha" })
    }
  }
  async function addComponent(p: MappingSearchRow) {
    if (!shell || selected == null) return
    try {
      await addBomComponent(shell, {
        structure_product_id: structureProductId,
        n_panels: selected,
        component_product_id: p.product_id,
        component_name: p.alt_name || p.name,
        component_sku: p.sku,
        qty: 1,
      })
      await load()
    } catch (err) {
      shell.bridge.toast({ variant: "error", description: err instanceof Error ? err.message : "Falha a adicionar" })
    }
  }
  function addPanelCount() {
    const n = Number(newCount)
    if (!Number.isInteger(n) || n <= 0) return
    setPanelCounts((prev) => Array.from(new Set([...prev, n])).sort((a, b) => a - b))
    setSelected(n)
    setNewCount("")
  }

  if (loading) {
    return (
      <div className="flex items-center gap-2 pl-8 text-xs text-muted-foreground">
        <Loader2 className="h-3 w-3 animate-spin" /> A carregar BOM…
      </div>
    )
  }

  return (
    <div className="space-y-2 pl-8">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-xs font-medium text-muted-foreground">Nº de painéis:</span>
        {allCounts.map((n) => (
          <button
            key={n}
            onClick={() => setSelected(n)}
            className={cn(
              "rounded-md border px-2 py-0.5 text-xs",
              selected === n ? "border-primary bg-primary/10 font-medium text-primary" : "border-border hover:bg-muted",
            )}
          >
            {n}
          </button>
        ))}
        <div className="flex items-center gap-1">
          <Input
            value={newCount}
            onChange={(e) => setNewCount(e.target.value.replace(/[^0-9]/g, ""))}
            onKeyDown={(e) => e.key === "Enter" && addPanelCount()}
            placeholder="+ nº"
            className="h-6 w-14 text-xs"
          />
          <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={addPanelCount} disabled={!newCount}>
            Add
          </Button>
        </div>
      </div>

      {selected == null ? (
        <p className="text-xs text-muted-foreground">Adiciona um nº de painéis para começar.</p>
      ) : (
        <>
          <div className="overflow-hidden rounded-md border border-border bg-background">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-32">SKU</TableHead>
                  <TableHead>Componente</TableHead>
                  <TableHead className="w-24 text-center">Qtd</TableHead>
                  <TableHead className="w-12 text-right">Rem.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {forCount.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-xs text-muted-foreground">
                      Sem componentes para {selected} painéis. Adiciona abaixo.
                    </TableCell>
                  </TableRow>
                ) : (
                  forCount.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="font-mono text-xs">
                        {r.component_sku || <span className="text-muted-foreground">—</span>}
                        {r.component_product_id == null && (
                          <AlertTriangle className="ml-1 inline h-3 w-3 text-amber-500" />
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{r.component_name}</TableCell>
                      <TableCell className="text-center">
                        <Input
                          type="number"
                          min={1}
                          step={1}
                          defaultValue={r.qty}
                          onBlur={(e) => saveQty(r, Math.round(Number(e.target.value)))}
                          className="mx-auto h-7 w-16 text-center text-xs"
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => remove(r)} title="Remover">
                          <Trash2 className="h-3.5 w-3.5 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <ProductSearch
            shell={shell}
            onPick={addComponent}
            placeholder="Adicionar componente — nome ou SKU…"
            taken={(p) => (forCount.some((r) => r.component_product_id === p.product_id) ? "neste grupo" : null)}
            hint={(p) => (p.current_role ? `já em ${p.current_role}` : null)}
          />
        </>
      )}
    </div>
  )
}

